import { reactive } from "vue";
import { toast } from "vue3-toastify";

import {
  ACCOUNT_DELETION_STAGES,
  accountAdministrationService,
  describeAccountAdministrationError,
} from "@/services/accountAdministrationService";
import { timeToRead } from "@/utils/toastUtils";

// Follows an account deletion from the moment it is confirmed until the
// server finishes, independently of the dialog that started it. The dialog
// renders the live stage; closing it (or the tab) does not stop the server,
// and the outcome is announced with a toast — immediately while the page is
// open, or on the next visit for deletions an administrator started.

export const ACCOUNT_DELETION_STAGE_LABELS = Object.freeze({
  checking: "Checking organisations, Wallets and messages",
  locking: "Locking the account so nothing changes during deletion",
  money: "Deleting money and handing over organisations",
  items: "Closing products, services and posts",
  messages: "Replacing messages with placeholders",
  following: "Removing follows and notifications",
  identity: "Removing the public profile",
  memberships: "Removing organisation memberships",
  content: "Deleting uploaded media, products and services",
  profile: "Deleting profile data",
  "sign-in": "Deleting the sign-in",
});

const STORAGE_KEY = "unit.pendingAccountDeletions.v1";
const POLL_INTERVAL_MS = 1500;
const MAX_TRACKING_MS = 15 * 60_000;
const MAX_CONSECUTIVE_POLL_ERRORS = 5;

function readPending(storage) {
  try {
    const value = JSON.parse(storage?.getItem(STORAGE_KEY) || "[]");
    return Array.isArray(value) ? value : [];
  } catch (_error) {
    return [];
  }
}

function writePending(storage, entries) {
  try {
    if (entries.length === 0) storage?.removeItem(STORAGE_KEY);
    else storage?.setItem(STORAGE_KEY, JSON.stringify(entries));
  } catch (_error) {
    // Browser storage is a convenience; the deletion itself is server-owned.
  }
}

function defaultStorage() {
  try {
    return globalThis.localStorage || null;
  } catch (_error) {
    return null;
  }
}

export function createAccountDeletionProgressTracker({
  service = accountAdministrationService,
  notify = toast,
  storage = defaultStorage(),
  now = () => Date.now(),
  schedule = (callback, delay) => setTimeout(callback, delay),
} = {}) {
  const deletions = reactive({});

  const successMessage = (entry) =>
    entry.mode === "self"
      ? "Your account was deleted."
      : `${entry.subjectLabel}'s account was deleted.`;
  const failureMessage = (entry, detail) =>
    entry.mode === "self"
      ? `Your account could not be deleted. ${detail}`
      : `${entry.subjectLabel}'s account could not be deleted. ${detail}`;

  const forget = (operationId) =>
    writePending(
      storage,
      readPending(storage).filter((pending) => pending.operationId !== operationId)
    );

  function finish(entry, outcome, error = null) {
    if (entry.state !== "running") return;
    entry.state = outcome;
    forget(entry.operationId);
    if (outcome === "completed") {
      entry.stageIndex = entry.stageCount;
      entry.stage = null;
      const message = successMessage(entry);
      notify.success(message, { autoClose: timeToRead(message) });
    } else {
      entry.reason = error?.reason || entry.reason || null;
      entry.errorMessage = error
        ? describeAccountAdministrationError(error)
        : "The deletion stopped before it finished. Try again.";
      // A changed impact sends the reviewer back to the refreshed notice
      // instead of reporting a failure.
      if (entry.reason !== "impact-changed") {
        const message = failureMessage(entry, entry.errorMessage);
        notify.error(message, { autoClose: timeToRead(message) });
      }
    }
    entry.resolve?.(entry);
  }

  function applyProgress(entry, progress) {
    if (progress.state === "running" && progress.stage) {
      entry.stage = progress.stage;
      entry.stageIndex = progress.stageIndex;
      entry.stageCount = progress.stageCount;
    } else if (progress.state === "completed") {
      finish(entry, "completed");
    } else if (progress.state === "failed" && !entry.callPending) {
      finish(entry, "failed", { reason: progress.reason });
    }
  }

  async function poll(entry) {
    if (entry.state !== "running") return;
    try {
      const progress = await service.getAccountDeletionProgress({
        uid: entry.mode === "admin" ? entry.uid : null,
        operationId: entry.operationId,
      });
      entry.pollErrors = 0;
      applyProgress(entry, progress);
    } catch (_error) {
      entry.pollErrors += 1;
    }
    if (entry.state !== "running") return;
    const expired = now() - entry.startedAt > MAX_TRACKING_MS;
    if (!entry.callPending && (expired || entry.pollErrors >= MAX_CONSECUTIVE_POLL_ERRORS)) {
      finish(entry, "failed");
      return;
    }
    schedule(() => poll(entry), POLL_INTERVAL_MS);
  }

  function track({ mode, uid, subjectLabel, operationId, startedAt = now() }) {
    const entry = reactive({
      mode,
      uid,
      subjectLabel: subjectLabel || "This account",
      operationId,
      startedAt,
      state: "running",
      stage: ACCOUNT_DELETION_STAGES[0],
      stageIndex: 0,
      stageCount: ACCOUNT_DELETION_STAGES.length,
      reason: null,
      errorMessage: "",
      callPending: false,
      pollErrors: 0,
      resolve: null,
    });
    entry.done = new Promise((resolve) => {
      entry.resolve = resolve;
    });
    deletions[operationId] = entry;
    return entry;
  }

  // run() performs the deletion call; its result is authoritative while the
  // page is open. If the call is cut off (timeout, lost connection) the
  // server may still be working, so progress decides the outcome.
  function start({ mode, uid, subjectLabel, operationId, adminUid = null, run }) {
    const existing = deletions[operationId];
    if (existing?.state === "running") return existing;
    const entry = track({ mode, uid, subjectLabel, operationId });
    if (mode === "admin" && adminUid) {
      writePending(storage, [
        ...readPending(storage).filter((pending) => pending.operationId !== operationId),
        { adminUid, uid, subjectLabel: entry.subjectLabel, operationId, startedAt: entry.startedAt },
      ]);
    }
    entry.callPending = true;
    Promise.resolve()
      .then(run)
      .then(
        () => {
          entry.callPending = false;
          finish(entry, "completed");
        },
        async (error) => {
          entry.callPending = false;
          if (error?.reason) {
            finish(entry, "failed", error);
            return;
          }
          try {
            const progress = await service.getAccountDeletionProgress({
              uid: mode === "admin" ? uid : null,
              operationId,
            });
            if (progress.state === "running") return; // polling continues
            if (progress.state === "completed") finish(entry, "completed");
            else finish(entry, "failed", progress.reason ? { reason: progress.reason } : error);
          } catch (_progressError) {
            finish(entry, "failed", error);
          }
        }
      );
    schedule(() => poll(entry), POLL_INTERVAL_MS);
    return entry;
  }

  // Deletions this administrator started in an earlier visit are followed to
  // their outcome, then announced.
  function resume(adminUid) {
    if (!adminUid) return;
    const cutoff = now() - MAX_TRACKING_MS;
    const pending = readPending(storage);
    const kept = pending.filter((entry) => entry.startedAt >= cutoff);
    if (kept.length !== pending.length) writePending(storage, kept);
    for (const saved of kept) {
      if (saved.adminUid !== adminUid || deletions[saved.operationId]) continue;
      const entry = track({
        mode: "admin",
        uid: saved.uid,
        subjectLabel: saved.subjectLabel,
        operationId: saved.operationId,
        startedAt: saved.startedAt,
      });
      poll(entry);
    }
  }

  return Object.freeze({
    deletions,
    start,
    resume,
    get: (operationId) => deletions[operationId] || null,
  });
}

export const accountDeletionProgressTracker = createAccountDeletionProgressTracker();
